import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { MemberCard, EmptyState } from '../components/ui/Cards';
import SafeIcon from '../common/SafeIcon';
import EscarapelaIcon from '../components/ui/EscarapelaIcon';
import SolDivider from '../components/ui/SolDivider';
import { motion } from 'framer-motion';

export default function Directorio() {
  const { members, CAPITULOS, currentUser, abanderadoVote, castAbanderadoVote } = useApp();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCapitulo, setFilterCapitulo] = useState('');
  
  // Voting State
  const [selectedVoteMember, setSelectedVoteMember] = useState('');

  const approvedMembers = members.filter(m => m.status === 'approved');
  const otherMembers = approvedMembers.filter(m => m.id !== currentUser.id);

  const filteredMembers = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return approvedMembers.filter(m => {
      const matchSearch = (m.nombre || '').toLowerCase().includes(q) ||
                          (m.rubro || '').toLowerCase().includes(q) ||
                          (m.ofrezco || '').toLowerCase().includes(q);
      const matchCapitulo = filterCapitulo ? m.capitulo_id === filterCapitulo : true;
      return matchSearch && matchCapitulo;
    });
  }, [approvedMembers, searchTerm, filterCapitulo]);

  const handleVote = async (e) => {
    e.preventDefault();
    if (!selectedVoteMember) return;
    try {
      await castAbanderadoVote(selectedVoteMember);
    } catch (err) {
      console.error('castAbanderadoVote failed:', err);
    }
  };

  const daysRemaining = 5; // Hardcoded mock for UI

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-10">
      
      {/* Hero Section with Argentine Flag Treatment */}
      <div className="relative">
        <div className="absolute -inset-x-4 md:-inset-x-10 -top-4 md:-top-10 h-[380px] overflow-hidden -z-10 rounded-b-[40px] md:rounded-b-[80px]">
          {/* Celeste Flag Wash Gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#74ACDF]/30 via-[#74ACDF]/10 to-[#FAF9F6]/0 z-0" />
          
          {/* Sol de Mayo Watermark */}
          <div className="absolute top-1/2 left-3/4 -translate-y-1/2 -translate-x-1/2 w-[600px] h-[600px] text-[#F6B40E] opacity-10 pointer-events-none animate-[spin_180s_linear_infinite]">
            <svg viewBox="0 0 200 200" fill="currentColor">
              <circle cx="100" cy="100" r="30" />
              {[...Array(16)].map((_, i) => (
                <path key={i} d="M96 5 L104 5 L100 65 Z" transform={`rotate(${i * 22.5} 100 100)`} />
              ))}
            </svg>
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pt-4 md:pt-10">
          <div className="max-w-2xl relative z-10">
            <span className="label-technical text-[#F6B40E] mb-2 block drop-shadow-sm">Directorio de Miembros</span>
            <h1 className="text-4xl md:text-6xl font-editorial font-bold text-[#1B2A4E] leading-tight">
              Conectá con la <span className="text-[#74ACDF] italic">comunidad.</span>
            </h1>
            <p className="text-lg text-[#1B2A4E]/70 mt-4 max-w-lg font-medium">
              Buscá y encontrá argentinos compartiendo camino en USA. {approvedMembers.length} empresarios y profesionales listos para colaborar.
            </p>
          </div>
        </div>

        {/* Voting Widget (Abanderado del Mes) */}
        <div className="mt-8 bg-white p-6 md:p-8 rounded-[32px] shadow-sm border border-[#F6B40E]/30 relative overflow-hidden z-20 flex flex-col md:flex-row items-center gap-8">
          <div className="absolute top-0 left-0 w-2 h-full bg-[#F6B40E]"></div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-white border border-stone-200 flex items-center justify-center shadow-sm">
                <EscarapelaIcon className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-editorial font-bold text-[#1B2A4E]">Votá al Abanderado del Mes</h3>
            </div>
            <p className="text-sm text-stone-500 mb-2">
              Elegimos a aquel miembro que más representa los valores de la comunidad aportando valor genuino.
            </p>
            <Link to="/insignias" className="text-xs font-bold text-[#74ACDF] hover:text-[#1B2A4E] transition-colors label-technical flex items-center gap-1.5">
              Ver significado de las insignias <SafeIcon name="ArrowRight" className="w-3 h-3" />
            </Link>
          </div>
          
          <div className="w-full md:w-auto bg-[#FAF9F6] p-4 rounded-2xl border border-stone-100 flex-shrink-0 min-w-[300px]">
            {abanderadoVote ? (
              <div className="text-center py-2">
                <div className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <SafeIcon name="Check" className="w-5 h-5" />
                </div>
                <p className="font-bold text-[#1B2A4E] text-sm">¡Gracias por tu voto!</p>
                <p className="text-xs text-stone-500 mt-1">El ganador se anunciará en {daysRemaining} días.</p>
              </div>
            ) : (
              <form onSubmit={handleVote} className="flex flex-col gap-3">
                <div className="flex items-center justify-between mb-1">
                  <label className="label-technical text-stone-400">Elegir Miembro</label>
                  <span className="text-xs font-bold text-[#F6B40E]">Quedan {daysRemaining} días</span>
                </div>
                <select 
                  required
                  className="w-full p-3 rounded-xl bg-white border border-stone-200 text-sm font-medium outline-none focus:ring-2 focus:ring-[#F6B40E]/50"
                  value={selectedVoteMember}
                  onChange={(e) => setSelectedVoteMember(e.target.value)}
                >
                  <option value="" disabled>Seleccionar un colega...</option>
                  {otherMembers.map(m => (
                    <option key={m.id} value={m.id}>{m.nombre} - {m.rubro}</option>
                  ))}
                </select>
                <button type="submit" className="w-full bg-[#1B2A4E] text-white py-3 rounded-xl text-sm font-bold shadow-md hover:bg-[#253969] transition-colors">
                  Emitir Voto
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Filters Float */}
        <div className="mt-8 bg-white p-2 rounded-2xl shadow-xl shadow-stone-200/50 border border-stone-100 flex flex-col md:flex-row gap-2 relative z-20">
          <div className="flex-1 relative">
            <SafeIcon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 w-5 h-5" />
            <input 
              type="text" 
              placeholder="¿A quién buscás hoy? (Ej: Marketing, LLC, Miami...)" 
              className="w-full pl-12 pr-4 py-4 rounded-xl border-none bg-stone-50 focus:ring-2 focus:ring-[#74ACDF]/20 outline-none text-[#1B2A4E]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            className="md:w-64 py-4 px-6 rounded-xl border-none bg-stone-50 text-[#1B2A4E] font-medium outline-none focus:ring-2 focus:ring-[#74ACDF]/20"
            value={filterCapitulo}
            onChange={(e) => setFilterCapitulo(e.target.value)}
          >
            <option value="">Todos los Capítulos</option>
            {CAPITULOS.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>
      </div>

      <SolDivider />

      {/* Grid */}
      {filteredMembers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
          {filteredMembers.map((member, idx) => (
            <motion.div key={member.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}>
              <MemberCard member={member} capituloName={CAPITULOS.find(c => c.id === member.capitulo_id)?.nombre} />
            </motion.div>
          ))}
        </div>
      ) : (
        <EmptyState icon="Users" title="Sin resultados" message="No encontramos miembros que coincidan con tu búsqueda. Intentá con términos más generales." />
      )}

      {/* Bottom Navy Band */}
      <div className="mt-20 p-12 bg-[#1B2A4E] rounded-[40px] text-white text-center relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32" />
        <div className="relative z-10">
          <h2 className="text-3xl font-editorial font-bold mb-4">¿Querés que tu nombre esté acá?</h2>
          <p className="text-[#74ACDF] mb-8 max-w-md mx-auto font-medium">Sumate al movimiento y empezá a conectar con otros emprendedores argentinos.</p>
          <button className="bg-[#F6B40E] hover:bg-[#e5a70d] text-[#1B2A4E] font-bold py-4 px-10 rounded-2xl transition-all hover:scale-105 shadow-xl">
            Postularme al Movimiento
          </button>
        </div>
      </div>

    </motion.div>
  );
}